// @flow
import { ResourceFormContext } from 'components/ResourceFill/providers/ResourceFormProvider'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import { ResourceStripeContext } from 'components/ResourceFill/providers/ResourceStripeProvider'
import ZentakeForm from 'components/ZentakeForm/ZentakeForm'
import type { UseSignatureMemo } from 'hooks/useSignatureMemo'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import type { Answer } from 'types/answer'
import FormContainer from 'ui/atoms/FormContainer'

import { OfficeBanner } from '@/components/OfficeBanner'
import BreadcrumbButton from '@/ui/atoms/BreadcrumbButton'
import cn from '@/utils/tailwind/cn'
type Props = { signatureMemo: ?UseSignatureMemo }

function ResourceFillBodyForm({ signatureMemo }: Props): React.Node {
  const history = useHistory()
  const { mode } = React.useContext(ResourceSharedContext) || {}
  const { response, setResponse, sessions } =
    React.useContext(ResourceResponseContext) || {}
  const formContext = React.useContext(ResourceFormContext)
  const stripeContext = React.useContext(ResourceStripeContext)
  const questions = React.useMemo(() => {
    if (!Object.keys(response?.form || {})?.length) return []
    const questions = []

    response?.form?.pages?.forEach((page) => {
      page.sections.forEach((section) => {
        section.rows.forEach((row) => {
          questions.push(...(row.questions || []))
        })
      })
    })

    return questions
  }, [response?.form])

  if (!response?.form) return null
  if (!formContext) return null
  if (!stripeContext) return null

  const { setStripeEmpty, setStripeError, stripeEmpty, stripeError } =
    stripeContext

  const {
    getResponseQuestionError,
    isResponseQuestionValid,
    isQuestionDirty,
    onAnswerChanged,
    onAnswerSignatureChanged,
    onAnswerCellChanged,
    setQuestionIdToScroll,
    questionIdToScroll,
  } = formContext

  const emptyAnswers: Array<Answer> = []

  const packetResponse = response?.packet_response

  const backPathId = packetResponse || response?.responses?.[0]?.id

  const backPathResponseType = packetResponse ? 'packet' : 'form'

  const backPath = backPathId
    ? `/response/${backPathId}?response_type=${backPathResponseType}`
    : '/'

  const isPreviewPage = history.location.pathname.includes('preview')

  const formOfficeUse = history.location.pathname.includes(
    'fillFormOfficeUseQuestions'
  )

  const packetOfficeUse = history.location.pathname.includes(
    'fillPacketOfficeUseQuestions'
  )

  const officeMode = formOfficeUse || packetOfficeUse

  const formContainerStyles = {
    'pb-[82px]': !packetOfficeUse,
  }

  return (
    <>
      {officeMode ? (
        <OfficeBanner.Root>
          <BreadcrumbButton
            className="m-0 !ml-6"
            onClick={() => history.push(backPath)}
          >
            Back
          </BreadcrumbButton>
          <OfficeBanner.Content>
            <OfficeBanner.Icon />
            You are completing office use questions
          </OfficeBanner.Content>
        </OfficeBanner.Root>
      ) : null}
      {isPreviewPage ? (
        <div className="sticky top-3 z-10 -mb-13 mt-4 flex w-full justify-center">
          <div className="flex w-full max-w-[900px] items-center justify-center gap-2 rounded border border-solid !border-orange-200 bg-orange-50 px-4 py-2.5 text-center font-medium text-[#ffaa00]">
            This is a preview page
          </div>
        </div>
      ) : null}
      <FormContainer className={cn(formContainerStyles)}>
        <div className="mb-8 w-full px-6 text-center">
          <h3 className="word-break-break-word inline-block text-4xl font-medium text-gray-700">
            {response?.form?.name}
          </h3>
        </div>
        <div className="flex flex-col">
          <ZentakeForm
            hideName
            answers={response?.answers || emptyAnswers}
            form={response?.form}
            getQuestionError={getResponseQuestionError}
            isQuestionDirty={isQuestionDirty}
            isQuestionValid={isResponseQuestionValid}
            mode={mode}
            onAnswerCellChanged={onAnswerCellChanged}
            onAnswerChanged={onAnswerChanged}
            onAnswerSignatureChanged={onAnswerSignatureChanged}
            questionIdToScroll={questionIdToScroll}
            questions={questions}
            response={response}
            sessions={sessions}
            setQuestionIdToScroll={setQuestionIdToScroll}
            setResponse={setResponse}
            setStripeEmpty={setStripeEmpty}
            setStripeError={setStripeError}
            signatureMemo={signatureMemo}
            stripeEmpty={stripeEmpty}
            stripeError={stripeError}
            templateId={formContext?.templateId}
          />
        </div>
      </FormContainer>
    </>
  )
}

export default ResourceFillBodyForm
